/* eslint-disable react/prop-types, prettier/prettier */
import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import AbcIcon from '@mui/icons-material/Abc'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import SaveIcon from '@mui/icons-material/Save'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { keyframes } from '@emotion/react'
import DOMPurify from 'isomorphic-dompurify'
import AIService from '../../services/aiService'
import GlowButton from '../../components/shared/GlowButton'
import CopyButton from '../../components/shared/CopyButton'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { variableExample } from '../../helpers/templateForm'
import { channelLabel, receiverLabel } from '../../helpers/aiHandoff'
import { useToolRun } from '../../helpers/toolRun'
import {
    ACCENT,
    CATEGORY,
    FONT,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    SHADOW,
    STATUS,
    SURFACE,
    TEXT,
    cardSx,
    menuProps,
    pageSx,
    tint,
} from '../../theme/tokens'

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`

const CHANNELS = ['whatsapp', 'email', 'sms']
const RECEIVER_TYPES = ['batch_student', 'parents', 'teacher', 'demo_student']

// Each tone's colour: text on its own tint (the chip) and the card's top
// rule, in both themes (contrast.ai-a.test.js).
export const TONE_COLORS = {
    Professional: CATEGORY.blue,
    Friendly: CATEGORY.green,
    Urgent: STATUS.error.fg,
}

function VariantCard({ variant, channel, index, navigate }) {
    const color = TONE_COLORS[variant.tone] || CATEGORY.orange
    return (
        <Paper
            elevation={0}
            sx={{
                background: SURFACE.card,
                border: `1px solid ${tint(color, 0.25)}`,
                borderTop: `3px solid ${color}`,
                borderRadius: `${RADIUS.lg}px`,
                boxShadow: SHADOW.card,
                p: 2.5,
                animation: `${fadeIn} ${0.2 + index * 0.1}s ease`,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={variant.tone}
                        size="small"
                        sx={{
                            background: tint(color, 0.13),
                            color,
                            border: `1px solid ${tint(color, 0.33)}`,
                            typography: 'caption',
                            fontWeight: 700,
                        }}
                    />
                    <Typography variant="caption" sx={{ color: TEXT.muted }}>
                        {variant.char_count} chars
                    </Typography>
                </Box>
                <CopyButton text={variant.body ?? ''} />
            </Box>

            <Typography
                variant="caption"
                component="p"
                sx={{ color: TEXT.muted, fontStyle: 'italic' }}
            >
                {variant.tone_description}
            </Typography>

            {channel === 'email' && variant.subject && (
                <Box
                    sx={{
                        background: SURFACE.subtle,
                        borderRadius: `${RADIUS.sm}px`,
                        px: 1.5,
                        py: 0.75,
                    }}
                >
                    <Typography
                        variant="overline"
                        component="p"
                        sx={{ color: TEXT.muted, mb: 0.25 }}
                    >
                        SUBJECT
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: TEXT.secondary, fontWeight: 500 }}
                    >
                        {variant.subject}
                    </Typography>
                </Box>
            )}

            <Box
                sx={{
                    background: SURFACE.sunken,
                    borderRadius: `${RADIUS.sm}px`,
                    p: 1.75,
                    border: `1px solid ${SURFACE.border}`,
                    flex: 1,
                }}
            >
                {channel === 'email' ? (
                    <Box
                        sx={{
                            typography: 'caption',
                            color: TEXT.secondary,
                            lineHeight: 1.6,
                        }}
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(variant.body ?? ''),
                        }}
                    />
                ) : (
                    <Typography
                        variant="body2"
                        sx={{
                            color: TEXT.secondary,
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.65,
                            fontFamily: FONT.mono,
                        }}
                    >
                        {variant.body}
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                <Tooltip title="Save as Template">
                    <IconButton
                        size="small"
                        aria-label="Save as template"
                        onClick={() =>
                            navigate('/create-template', {
                                state: {
                                    body: variant.body,
                                    channel: channel,
                                    subject: variant.subject || '',
                                },
                            })
                        }
                        sx={{
                            color: TEXT.muted,
                            '&:hover': {
                                color: ACCENT.strong,
                                background: ACCENT.bg08,
                            },
                        }}
                    >
                        <SaveIcon sx={{ fontSize: ICON.md }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Check Risk">
                    <IconButton
                        size="small"
                        aria-label="Check unsubscribe risk"
                        onClick={() =>
                            navigate('/ai/unsubscribe-risk', {
                                state: {
                                    template_body: variant.body,
                                    channel: channel,
                                },
                            })
                        }
                        sx={{
                            color: TEXT.muted,
                            '&:hover': {
                                color: ACCENT.strong,
                                background: ACCENT.bg08,
                            },
                        }}
                    >
                        <GppMaybeIcon sx={{ fontSize: ICON.md }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Preview">
                    <IconButton
                        size="small"
                        aria-label="Preview messages"
                        onClick={() =>
                            navigate('/ai/bulk-preview', {
                                state: { template_body: variant.body, channel },
                            })
                        }
                        sx={{
                            color: TEXT.muted,
                            '&:hover': {
                                color: ACCENT.strong,
                                background: ACCENT.bg08,
                            },
                        }}
                    >
                        <VisibilityIcon sx={{ fontSize: ICON.md }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    )
}

export default function ABVariants() {
    const navigate = useNavigate()
    const location = useLocation()
    const [description, setDescription] = useState('')
    const [channel, setChannel] = useState('whatsapp')
    const [receiverType, setReceiverType] = useState('batch_student')
    const [variableInput, setVariableInput] = useState('')
    const [variables, setVariables] = useState([])
    const [loading, setLoading] = useState(false)
    const [variants, setVariants] = useState(null)
    // The channel the shown variants were written for: the select can change
    // afterwards, and their buttons must hand off the text's own channel.
    const [variantsChannel, setVariantsChannel] = useState(channel)
    const [error, setError] = useState('')
    const run = useToolRun('ab-variants')

    useEffect(() => {
        // Reset outputs so re-runs (navigation with new state) behave predictably
        setVariants(null)
        setError('')
        const saved = run.recall()
        if (saved) {
            setDescription(saved.description ?? '')
            setChannel(saved.channel)
            setReceiverType(saved.receiverType)
            setVariables(saved.variables ?? [])
            setVariantsChannel(saved.channel)
            setVariants(saved.variants)
            return
        }
        const state = location.state
        if (state?.description) {
            setDescription(state.description)
        } else {
            setDescription(
                'Remind batch students their Maths class starts in 30 minutes and encourage them to join on time'
            )
        }
        if (state?.channel && CHANNELS.includes(state.channel)) {
            setChannel(state.channel)
        }
    }, [location.state])

    /**
     * Add what is typed in the variables box (comma-separated names, braces
     * stripped) to the list and clear the box; returns the resulting list.
     *
     * Runs on Enter, on leaving the box and before Generate: a name typed
     * without Enter used to be dropped silently.
     */
    const commitVariables = () => {
        const typed = variableInput
            .split(',')
            .map((v) => v.trim().replace(/[{}$]/g, ''))
            .filter(Boolean)
        const next = [...variables]
        typed.forEach((v) => {
            if (!next.includes(v)) next.push(v)
        })
        if (typed.length) setVariables(next)
        setVariableInput('')
        return next
    }

    const addVariable = (e) => {
        if (e.key === 'Enter') commitVariables()
    }

    const handleGenerate = async () => {
        if (!description.trim()) return
        const allVariables = commitVariables()
        setLoading(true)
        setError('')
        setVariants(null)
        run.forget()
        try {
            const res = await AIService.templateVariants({
                description,
                channel,
                receiver_type: receiverType,
                variables: allVariables,
            })
            const generated = res.data?.result?.variants ?? []
            setVariantsChannel(channel)
            setVariants(generated)
            if (generated.length)
                run.remember({
                    description,
                    channel,
                    receiverType,
                    variables: allVariables,
                    variants: generated,
                })
        } catch {
            setError('Failed to generate variants. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 3, md: 5 } }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            background: GRADIENT.brand,
                            borderRadius: `${RADIUS.md}px`,
                            p: 1,
                            display: 'flex',
                        }}
                    >
                        {/* Deep Blue on the orange-to-yellow badge in both
                            themes (Almost White on its yellow end is 1.8:1). */}
                        <AbcIcon sx={{ color: ON_ORANGE, fontSize: ICON.lg }} />
                    </Box>
                    <Box>
                        <Typography
                            variant="h3"
                            component="p"
                            sx={{ color: TEXT.primary }}
                        >
                            A/B Variants
                        </Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{ color: TEXT.secondary }}
                        >
                            Generate 3 tone variants of the same message —
                            Professional, Friendly, Urgent
                        </Typography>
                    </Box>
                </Box>

                {/* Form */}
                <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 3 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <TextField
                            select
                            label="Channel"
                            value={channel}
                            onChange={(e) => setChannel(e.target.value)}
                            size="small"
                            fullWidth
                            SelectProps={{ MenuProps: menuProps }}
                        >
                            {CHANNELS.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {channelLabel(c)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Receiver Type"
                            value={receiverType}
                            onChange={(e) => setReceiverType(e.target.value)}
                            size="small"
                            fullWidth
                            SelectProps={{ MenuProps: menuProps }}
                        >
                            {RECEIVER_TYPES.map((r) => (
                                <MenuItem key={r} value={r}>
                                    {receiverLabel(r)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <TextField
                        label="Variables (press Enter to add)"
                        value={variableInput}
                        onChange={(e) => setVariableInput(e.target.value)}
                        onKeyDown={addVariable}
                        onBlur={commitVariables}
                        size="small"
                        fullWidth
                        placeholder="e.g. name, batch_name, topic"
                        sx={{ mb: variables.length ? 1 : 0 }}
                    />
                    {variables.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.75,
                                mb: 2,
                            }}
                        >
                            {variables.map((v) => (
                                <Chip
                                    key={v}
                                    label={variableExample(channel, v)}
                                    size="small"
                                    onDelete={() =>
                                        setVariables((prev) =>
                                            prev.filter((x) => x !== v)
                                        )
                                    }
                                    sx={{
                                        background: ACCENT.bg15,
                                        color: ACCENT.text,
                                        border: `1px solid ${ACCENT.border30}`,
                                        typography: 'caption',
                                    }}
                                />
                            ))}
                        </Box>
                    )}

                    <TextField
                        label="Describe the message goal"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                        placeholder="e.g. Remind students their batch session starts in 30 minutes and encourage them to join on time"
                        sx={{ mt: 2 }}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mt: 2,
                        }}
                    >
                        <GlowButton
                            onClick={handleGenerate}
                            disabled={!description.trim()}
                            loading={loading}
                            startIcon={<AutoAwesomeIcon />}
                        >
                            Generate 3 Variants
                        </GlowButton>
                    </Box>
                </Paper>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                {/* Results */}
                {variants && variants.length > 0 && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: '1fr 1fr 1fr',
                            },
                            gap: 2,
                        }}
                    >
                        {variants.map((v, i) => (
                            <VariantCard
                                key={v.tone}
                                variant={v}
                                channel={variantsChannel}
                                index={i}
                                navigate={navigate}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
